import { BarChart3, CheckCircle2, TrendingUp, X, Sparkles, DollarSign, LayoutDashboard, PieChart } from "lucide-react";
import { PMS_IMAGE } from "../constants";

const PMSSection = () => {
  return (
    <section data-testid="pms-section" className="py-14 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#FF6B00]/10 text-[#FF6B00] px-4 py-2 rounded-full text-sm font-bold mb-5 md:mb-6">
            <LayoutDashboard className="w-4 h-4" />
            專屬管理系統
          </div>
          <h2 className="text-2xl sm:text-4xl font-bold text-[#0A2540] tracking-tight mb-4">
            最新一代人客多 PMS 系統
          </h2>

          {/* 大字強調：台灣第一個會幫你民宿帶業績的系統解決方案 */}
          <div className="relative max-w-3xl mx-auto mb-6 md:mb-8">
            <div className="bg-gradient-to-r from-[#FF6B00] via-[#E65A00] to-[#FF6B00] rounded-2xl p-5 md:p-7 shadow-xl shadow-[#FF6B00]/30 border-2 border-white ring-2 ring-[#FF6B00]/40">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-2xl">🇹🇼</span>
                <span className="text-[10px] md:text-xs text-white/90 font-bold uppercase tracking-[0.15em]">Taiwan No.1</span>
                <span className="text-2xl">✨</span>
              </div>
              <div className="text-lg md:text-2xl lg:text-3xl font-black text-white leading-tight text-center">
                台灣<span className="text-[#F5C24A]">第一個</span>
                <span className="block md:inline"> 會幫你民宿<span className="underline decoration-[#F5C24A] decoration-4 underline-offset-4">帶業績</span>的</span>
                <span className="block md:inline"> 系統解決方案</span>
              </div>
            </div>
          </div>

          <p className="text-base md:text-lg text-[#475569] max-w-3xl mx-auto px-2 leading-relaxed">
            <span className="font-bold text-[#0A2540]">針對包棟專門設計，</span>突破過往旅宿單間 PMS 限制，讓住房收益貼合包棟產品提升
          </p>

          {/* OTA 整合資訊 */}
          <div className="mt-6 md:mt-8 max-w-3xl mx-auto">
            <div className="bg-gradient-to-r from-[#F8F9FA] to-white border border-slate-200 rounded-2xl p-4 md:p-5">
              <div className="text-xs md:text-sm text-[#475569] mb-2">
                與 <span className="font-bold text-[#0A2540]">上市公司奧丁丁、旭海</span> 深度整合，支援大多數主流 OTA 平台
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
                <span className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs md:text-sm font-bold text-[#0A2540] shadow-sm">Agoda</span>
                <span className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs md:text-sm font-bold text-[#0A2540] shadow-sm">Booking.com</span>
                <span className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs md:text-sm font-bold text-[#0A2540] shadow-sm">Trip.com</span>
                <span className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs md:text-sm font-bold text-[#475569] shadow-sm">＋ 更多 OTA</span>
              </div>
            </div>
          </div>
        </div>

        {/* 一般 PMS vs 人客多 PMS 對照 */}
        <div className="grid md:grid-cols-2 gap-5 md:gap-6 mb-10 md:mb-12">
          {/* 一般 PMS */}
          <div className="bg-white rounded-2xl p-5 md:p-8 border border-slate-200 relative">
            <div className="absolute top-3 md:top-4 right-3 md:right-4 bg-slate-200 text-slate-500 text-xs font-bold px-3 py-1 rounded-full">
              一般 PMS
            </div>
            <h3 className="text-lg md:text-xl font-bold text-slate-400 mb-5 md:mb-6 pr-20 md:pr-0">傳統 PMS 系統</h3>
            <div className="space-y-3 md:space-y-4">
              {[
                "針對單間房型設計，無法適配包棟經營",
                "無法進行包棟最佳定價分析",
                "住房率分析僅限單間維度",
                "無法有效處理空房問題",
                "帳務需人工逐筆對帳",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm md:text-base text-slate-400 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 人客多 PMS */}
          <div className="bg-white rounded-2xl p-5 md:p-8 border-2 border-[#FF6B00] relative shadow-lg">
            <div className="absolute top-3 md:top-4 right-3 md:right-4 bg-[#FF6B00] text-white text-xs font-bold px-3 py-1 rounded-full">
              人客多 PMS
            </div>
            <h3 className="text-lg md:text-xl font-bold text-[#0A2540] mb-5 md:mb-6 pr-24 md:pr-0">包棟專屬 PMS 系統</h3>
            <div className="space-y-3 md:space-y-4">
              {[
                "包棟專門設計，完全發揮包棟住房率效能",
                "包棟最佳定價分析，優化收益策略",
                "住房率、營收、訂單趨勢全面分析",
                "客單價提升方案包裝設計（客單價 +10–25%），有效解決空房問題",
                "專人協助收款整理，解決帳務地獄",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#FF6B00] flex-shrink-0 mt-0.5" />
                  <span className="text-sm md:text-base font-medium text-[#0A2540] leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AI 方案包裝 + PMS 截圖 */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            {/* 客單價提升方案包裝設計亮點（所有行銷公司與 OTA 都沒做的事） */}
            <div data-testid="package-design-highlight" className="relative bg-gradient-to-br from-[#0A2540] to-[#163859] rounded-3xl p-6 md:p-8 mb-6 md:mb-8 shadow-xl overflow-hidden">
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#FF6B00]/20 rounded-full blur-3xl"></div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-3 py-1.5 rounded-full text-xs md:text-sm font-black mb-4">
                  <Sparkles className="w-4 h-4" />
                  所有行銷公司與 OTA 都沒做的事
                </div>
                <h3 className="text-xl md:text-3xl font-black text-white leading-tight mb-2">
                  客單價提升方案包裝設計
                </h3>
                <div className="text-base md:text-lg font-black text-[#F5C24A] mb-4">客單價平均提升 10–25% · 已有多個成功案例</div>
                <p className="text-sm md:text-base text-white/80 leading-relaxed mb-5">
                  不是只把房間丟上網賣。我們依包棟客群重新包裝方案（餐食、體驗、加購、平日組合），配合 PMS 定價分析，
                  <span className="font-bold text-white">讓平日週一到週四也能達 30–90% 住房率，客單價還更高。</span>
                </p>
                <div className="grid grid-cols-3 gap-2 md:gap-3 mb-5">
                  {[["+10–25%", "客單價提升"], ["30–90%", "平日住房率"], ["多個", "成功案例"]].map(([v, l]) => (
                    <div key={l} className="bg-white/10 rounded-xl px-3 py-2.5 text-center">
                      <div className="text-lg md:text-2xl font-black text-[#F5C24A] leading-tight">{v}</div>
                      <div className="text-[11px] md:text-xs text-white/70 mt-0.5">{l}</div>
                    </div>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-2 md:gap-3">
                  <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <div className="text-xs font-black text-white/50 mb-1">一般行銷公司</div>
                    <div className="text-sm text-white/80 leading-snug">只做廣告幫忙賣，<span className="text-red-300 font-bold">不會重新設計方案</span></div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <div className="text-xs font-black text-white/50 mb-1">OTA 平台</div>
                    <div className="text-sm text-white/80 leading-snug">抽 15% 卻<span className="text-red-300 font-bold">不適合包棟</span>：不拍影片、不投廣、不回包棟客製化訊息</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3 md:space-y-4">
              {[
                { icon: <PieChart className="w-5 h-5" />, text: "完全發揮包棟住房率效能" },
                { icon: <BarChart3 className="w-5 h-5" />, text: "對帳清晰，營收報表一目了然" },
                { icon: <TrendingUp className="w-5 h-5" />, text: "訂單趨勢、營收統計、住房率分析" },
                { icon: <DollarSign className="w-5 h-5" />, text: "平均房價追蹤，優化定價策略" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3 md:gap-4 bg-white rounded-xl p-3 md:p-4 shadow-sm">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-[#FF6B00]/10 rounded-lg flex items-center justify-center text-[#FF6B00] flex-shrink-0">
                    {item.icon}
                  </div>
                  <span className="text-sm md:text-base font-medium text-[#0A2540] leading-snug">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200">
              <img src={PMS_IMAGE} alt="人客多 PMS 系統" className="w-full h-auto" />
            </div>
            <div className="absolute -bottom-3 right-3 md:-bottom-4 md:-right-4 bg-[#0A2540] text-white rounded-xl md:rounded-2xl px-4 md:px-6 py-2.5 md:py-4 shadow-xl">
              <div className="text-xs md:text-sm text-white/70">包棟專屬設計</div>
              <div className="text-sm md:text-xl font-bold">突破傳統 PMS 限制</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PMSSection;
